/**
 * PrefectureReport - Prefecture-level listener analytics
 * Shows aggregate stats + per-station breakdown with:
 * - Daypart filtering
 * - Market share
 * - Unified scoring (Real vs Estimated)
 * - Greek tooltips (positioned ABOVE cursor)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, Users, TrendingUp, TrendingDown, Radio, ChevronDown, ChevronUp, Loader2, Award, PieChart as PieIcon, Info, ArrowUp, ArrowDown, Minus, Star, Percent, Clock, Building2, Play, Pause, Sun, Sunrise, Sunset, Moon, Car, CalendarClock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { getBackendUrl } from '../utils/api';
import { useRadioPlayer } from '../contexts/RadioPlayerContext';

// ── Period presets ──────────────────────────────────────────────────
// All weeks are Monday–Sunday (ISO). "current" includes today-so-far.
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => {
  const r = new Date(d);
  const day = r.getDay(); // 0=Sun
  const offset = (day + 6) % 7;
  r.setDate(r.getDate() - offset);
  r.setHours(0, 0, 0, 0);
  return r;
};

const PERIOD_PRESETS = [
  { key: 'current_week',  label: 'Τρέχουσα εβδομάδα',   weeks: 1, mode: 'current' },
  { key: 'previous_week', label: 'Προηγούμενη εβδομάδα', weeks: 1, mode: 'previous' },
  { key: '2_weeks',       label: '2 εβδομάδες',          weeks: 2, mode: 'rolling' },
  { key: '3_weeks',       label: '3 εβδομάδες',          weeks: 3, mode: 'rolling' },
  { key: 'current_month', label: 'Τρέχων μήνας',         mode: 'month' },
];

// Returns { from, to, previousFrom, previousTo } as YYYY-MM-DD strings
// "previous" is the equivalent-length window right before this one (for trend).
const computePeriodRange = (preset) => {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  let from, to;

  if (preset.mode === 'current') {
    from = mondayOf(today);
    to = new Date(today);
  } else if (preset.mode === 'previous') {
    const thisMon = mondayOf(today);
    to = new Date(thisMon); to.setDate(to.getDate() - 1);
    from = new Date(to); from.setDate(from.getDate() - 6);
  } else if (preset.mode === 'rolling') {
    // last N full weeks (Mon–Sun), ending at the Sunday BEFORE this Monday
    const thisMon = mondayOf(today);
    to = new Date(thisMon); to.setDate(to.getDate() - 1);
    from = new Date(to); from.setDate(from.getDate() - (preset.weeks * 7 - 1));
  } else if (preset.mode === 'month') {
    from = new Date(today.getFullYear(), today.getMonth(), 1);
    to = new Date(today);
  }

  const length = Math.round((to - from) / 86400000) + 1;
  const prevTo = new Date(from); prevTo.setDate(prevTo.getDate() - 1);
  const prevFrom = new Date(prevTo); prevFrom.setDate(prevFrom.getDate() - (length - 1));

  return {
    from: ymd(from), to: ymd(to),
    previousFrom: ymd(prevFrom), previousTo: ymd(prevTo),
    length,
  };
};

const backendUrl = getBackendUrl();

const HOURS_LABELS = Array.from({length: 24}, (_, i) => `${String(i).padStart(2,'0')}:00`);

// Format activity index (same numeric basis as TLH) into human-readable form
const formatTLH = (hours) => {
  if (hours == null || isNaN(hours)) return '—';
  const h = Number(hours);
  if (h >= 1000) return `${(h / 1000).toFixed(1)}k ώρες`;
  if (h < 1) return `${Math.round(h * 60)}λ`;
  const whole = Math.floor(h);
  const mins = Math.round((h - whole) * 60);
  return mins > 0 ? `${whole}ώ ${mins}λ` : `${whole}ώρες`;
};

// Rank change indicator
const RankChange = ({ change, stationId }) => {
  const testId = `rank-change-${stationId || 'unknown'}`;
  if (change == null) return (
    <span data-testid={testId} className="inline-flex items-center gap-0.5 text-[10px] text-gray-500/60 font-medium" title="Νέα εγγραφή">
      <span className="w-2 h-2 rounded-full bg-gray-500/40" />
      νέος
    </span>
  );
  if (change === 0) return (
    <span data-testid={testId} className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 font-medium" title="Καμία μεταβολή">
      <Minus className="w-2.5 h-2.5" />
      0
    </span>
  );
  if (change > 0) return (
    <span data-testid={testId} className="inline-flex items-center gap-0.5 text-[10px] text-emerald-400 font-bold" title={`Ανέβηκε ${change} θέσεις`}>
      <ArrowUp className="w-2.5 h-2.5" />
      {change}
    </span>
  );
  return (
    <span data-testid={testId} className="inline-flex items-center gap-0.5 text-[10px] text-orange-400 font-bold" title={`Κατέβηκε ${Math.abs(change)} θέσεις`}>
      <ArrowDown className="w-2.5 h-2.5" />
      {Math.abs(change)}
    </span>
  );
};

const DAYPARTS = [
  { key: 'all',       label: 'Όλη μέρα',        short: 'Όλη',       icon: CalendarClock, range: '' },
  { key: 'morning',   label: 'Πρωί',            short: 'Πρωί',      icon: Sunrise,       range: '06–10' },
  { key: 'midday',    label: 'Μεσημέρι',        short: 'Μεσημέρι',  icon: Sun,           range: '10–15' },
  { key: 'afternoon', label: 'Απόγευμα',        short: 'Απόγευμα',  icon: Sunset,        range: '15–19' },
  { key: 'drive',     label: 'Drive',           short: 'Drive',     icon: Car,           range: '18–22' },
  { key: 'night',     label: 'Νύχτα',           short: 'Νύχτα',     icon: Moon,          range: '22–06' },
];

const SECTIONS = [
  { key: 'market_share',    label: 'Μερίδιο Αγοράς',   icon: Percent },
  { key: 'ranking',         label: 'Κατάταξη',         icon: Award },
  { key: 'daypart',         label: 'Ωριαία Ανάλυση',   icon: Clock },
  { key: 'station_details', label: 'Στοιχεία Σταθμού', icon: Building2 },
];

const TOOLTIPS = {
  peak: 'Μέγιστος αριθμός ταυτόχρονων ακροατών στο χρονικό διάστημα',
  avg: 'Μέσος όρος ακροατών ανά ώρα',
  min: 'Ελάχιστος αριθμός ακροατών που καταγράφηκε',
  tlh: 'Total Listening Hours — Συνολικές ώρες ακρόασης (αθροιστικά)',
  market_share: 'Μερίδιο αγοράς — Ποσοστό επί των συνολικών ωρών ακρόασης του νομού. Όταν ένας σταθμός δεν παρέχει δεδομένα ακροατών, εμφανίζεται ως εκτίμηση βάσει streaming signal.',
  samples: 'Πόσες μετρήσεις ελήφθησαν στο χρονικό διάστημα',
  stations: 'Πόσοι σταθμοί είχαν δεδομένα αυτή την ώρα',
};

const Tip = ({ text, children }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs bg-gray-900 text-gray-100 border-gray-700 z-[100]">
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const StatCard = ({ label, value, icon: Icon, color = "blue", tooltip }) => {
  const colors = {
    blue: "from-blue-500/20 to-blue-600/10 text-blue-400",
    green: "from-green-500/20 to-green-600/10 text-green-400",
    amber: "from-amber-500/20 to-amber-600/10 text-amber-400",
    purple: "from-purple-500/20 to-purple-600/10 text-purple-400",
  };
  const card = (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-lg p-3 border border-white/5 ${tooltip ? 'cursor-help' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-xs opacity-70">{label}</span>
        {tooltip && <Info className="w-3 h-3 opacity-40" />}
      </div>
      <div className="text-xl font-bold">{typeof value === 'number' ? value.toLocaleString('el-GR') : value}</div>
    </div>
  );
  return tooltip ? <Tip text={tooltip}>{card}</Tip> : card;
};

const MiniBarChart = ({ data, dataKey = "avg", maxVal, color = "#3b82f6", height = 120 }) => {
  if (!data || data.length === 0) return null;
  // Always clamp max to the actual max in the data so bars never overflow the container.
  const dataMax = Math.max(...data.map(d => d[dataKey] || 0), 1);
  const max = Math.max(maxVal || 0, dataMax);
  return (
    <div className="flex items-end gap-[2px] overflow-hidden" style={{ height }}>
      {data.map((d, i) => {
        const val = d[dataKey] || 0;
        const pct = Math.min(1, val / max);
        const h = Math.max(2, pct * height);
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${HOURS_LABELS[d.hour]}: ${val}`}>
            <div style={{ height: h, backgroundColor: color, minWidth: 4 }} className="rounded-t-sm w-full opacity-80 hover:opacity-100 transition-opacity" />
          </div>
        );
      })}
    </div>
  );
};

// Inline 24h sparkline — compact version for row headers
const Sparkline = ({ data, dataKey = "avg", color = "#3b82f6", width = 96, height = 22 }) => {
  if (!data || data.length === 0) return <div style={{ width, height }} />;
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const stepW = width / 24;
  const points = Array.from({ length: 24 }, (_, h) => {
    const row = data.find(d => d.hour === h);
    const v = row ? (row[dataKey] || 0) : 0;
    const y = height - Math.max(1, (v / max) * (height - 2));
    return `${h * stepW},${y.toFixed(1)}`;
  }).join(' ');
  // Build area polygon points (adds bottom corners for fill)
  const areaPts = `0,${height} ${points} ${(23 * stepW).toFixed(1)},${height}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="inline-block" aria-label="24h hourly trend">
      <polygon points={areaPts} fill={color} fillOpacity="0.15" />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.25"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

// Custom scrollable tooltip that can escape chart bounds.
// When there are many stations, it switches to a 2-column grid so the full
// list is always visible without scrolling or being clipped by the chart.
const TrendTooltip = ({ active, payload, label, colorMap }) => {
  if (!active || !payload || payload.length === 0) return null;
  const sorted = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
  const twoCols = sorted.length > 8;
  return (
    <div
      className="rounded-md border border-gray-700 shadow-2xl text-[11px] pointer-events-auto"
      style={{
        backgroundColor: 'rgba(17, 24, 39, 0.98)',
        maxHeight: 520,
        maxWidth: twoCols ? 520 : 280,
        overflowY: 'auto',
        padding: '6px 10px',
      }}
    >
      <div className="text-gray-300 font-semibold mb-1 sticky top-0 bg-gray-900 -mx-[10px] px-[10px] pb-1 border-b border-gray-700 z-[1]">
        {label}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: twoCols ? '1fr 1fr' : '1fr',
          columnGap: 14,
          rowGap: 0,
        }}
      >
        {sorted.map((p, i) => {
          const color = (colorMap && colorMap[p.dataKey]) || p.color || '#9ca3af';
          return (
            <div key={i} className="flex items-center justify-between gap-3 py-[2px]">
              <span className="inline-flex items-center gap-1.5 truncate" style={{ maxWidth: 170 }}>
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                <span className="text-gray-200 truncate">{p.dataKey}</span>
              </span>
              <span className="text-gray-400 shrink-0 tabular-nums">{p.value?.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Market Share day-by-day stacked bars (per station)
const MarketShareTrend = ({ data, stations, ownIds }) => {
  // Top 5 distinct colors, rest get a muted grey (Rainbow Chart style)
  const TOP_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  const MUTED_COLOR = '#4b5563';
  const MAX_COLORED = 5;

  // Build chart rows: one per day with { date, [stationName]: market_share }
  const chartData = data.map(day => {
    const row = { date: day.date?.slice(5) };
    (day.stations || []).forEach(s => {
      row[s.station_name] = s.market_share;
    });
    return row;
  });

  // Compute AVERAGE market share per station across the full period so we can
  // determine which stations get distinct colors (top 5) vs muted (rest).
  const avgByStation = {};
  (data || []).forEach(day => {
    (day.stations || []).forEach(s => {
      avgByStation[s.station_name] = (avgByStation[s.station_name] || 0) + (s.market_share || 0);
    });
  });
  const sortedNames = Object.keys(avgByStation).sort((a, b) => avgByStation[b] - avgByStation[a]);
  const topNames = new Set(sortedNames.slice(0, MAX_COLORED));
  const nameColor = {};
  let colorIdx = 0;
  sortedNames.forEach(name => {
    if (topNames.has(name)) {
      nameColor[name] = TOP_COLORS[colorIdx++ % TOP_COLORS.length];
    } else {
      nameColor[name] = MUTED_COLOR;
    }
  });

  const ownSet = new Set([...ownIds].map(String));
  const items = stations.map((s, i) => {
    const name = s.station_name || s.name || `Station ${i + 1}`;
    const sid = String(s.id || s.station_id || `s-${i}`);
    return {
      sid,
      name,
      color: nameColor[name] || MUTED_COLOR,
      isTop: topNames.has(name),
    };
  });
  // Stack order: muted stations at the bottom, top-5 at the top (better visual)
  items.sort((a, b) => (a.isTop ? 1 : 0) - (b.isTop ? 1 : 0));

  // Color map for tooltip lookup (by dataKey = station_name)
  const colorMap = {};
  items.forEach(it => { colorMap[it.name] = ownSet.has(it.sid) ? '#fb923c' : it.color; });

  return (
    <div className="p-3" style={{ position: 'relative', overflow: 'visible' }}>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} unit="%" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} allowDataOverflow={false} />
          <RTip
            allowEscapeViewBox={{ x: true, y: true }}
            wrapperStyle={{ zIndex: 9999, pointerEvents: 'auto', outline: 'none' }}
            position={{ y: -10 }}
            content={<TrendTooltip colorMap={colorMap} />}
            itemSorter={(item) => -(item.value || 0)}
          />
          {items.map(it => {
            const isOwn = ownSet.has(it.sid);
            return (
              <Bar
                key={it.sid}
                dataKey={it.name}
                stackId="a"
                fill={isOwn ? '#fb923c' : it.color}
                stroke={isOwn ? '#ea580c' : 'none'}
                strokeWidth={isOwn ? 1 : 0}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
      {/* Legend: top-5 colored + summary of muted */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[10px] text-gray-400">
        {items.filter(it => it.isTop || ownSet.has(it.sid)).map(it => {
          const isOwn = ownSet.has(it.sid);
          return (
            <span key={it.sid} className="inline-flex items-center gap-1">
              <span
                className="w-2 h-2 rounded-sm"
                style={{ backgroundColor: isOwn ? '#fb923c' : it.color }}
              />
              <span className={isOwn ? 'text-orange-300 font-semibold' : ''}>{it.name}</span>
            </span>
          );
        })}
        {items.some(it => !it.isTop && !ownSet.has(it.sid)) && (
          <span className="inline-flex items-center gap-1 text-gray-500">
            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: MUTED_COLOR }} />
            Άλλοι ({items.filter(it => !it.isTop && !ownSet.has(it.sid)).length}) — hover για λεπτομέρειες
          </span>
        )}
      </div>
    </div>
  );
};

// Aggregate market-share pie — shows the SAME numbers as Κατάταξη so users
// can sanity-check the day-by-day chart against the real 7d aggregate.
const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const PIE_MUTED = '#4b5563';

const MarketShareAveragePie = ({ stations }) => {
  if (!stations || stations.length === 0) return null;

  const sorted = [...stations]
    .filter(s => (s.market_share || 0) > 0)
    .sort((a, b) => (b.market_share || 0) - (a.market_share || 0));

  if (sorted.length === 0) {
    return (
      <div className="flex items-center justify-center text-xs text-gray-500 py-8">
        Δεν υπάρχουν δεδομένα για μέσο όρο
      </div>
    );
  }

  // Build pie data: keep all stations (colored top 5, grey rest)
  const data = sorted.map((s, i) => ({
    name: s.station_name,
    value: s.market_share || 0,
    fill: i < 5 ? PIE_COLORS[i] : PIE_MUTED,
    rank: s.rank,
  }));

  return (
    <div className="border-l border-white/5 lg:pl-3" data-testid="market-share-average-pie">
      <div className="flex items-center gap-2 px-1 pb-1 text-[10px] text-gray-400 font-semibold uppercase tracking-wide">
        <PieIcon className="w-3 h-3" />
        Μέσος όρος περιόδου
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={75}
            paddingAngle={1}
            stroke="#111827"
            strokeWidth={1}
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.fill} />
            ))}
          </Pie>
          <RTip
            contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 6, fontSize: 11 }}
            formatter={(v, n) => [`${v.toFixed(1)}%`, n]}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="px-1 space-y-0.5 max-h-[110px] overflow-y-auto text-[10px]">
        {sorted.slice(0, 8).map((s, i) => (
          <div key={s.station_id} className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 min-w-0">
              <span
                className="w-2 h-2 rounded-sm shrink-0"
                style={{ backgroundColor: i < 5 ? PIE_COLORS[i] : PIE_MUTED }}
              />
              <span className="text-gray-300 truncate">#{s.rank} {s.station_name}</span>
            </span>
            <span className="text-gray-400 font-mono">{(s.market_share || 0).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SPI_COLORS = {
  stable:   { dot: 'bg-emerald-400', ring: 'ring-emerald-400/30', label: 'Σταθερό stream' },
  moderate: { dot: 'bg-amber-400',   ring: 'ring-amber-400/30',   label: 'Μέτρια σταθερότητα stream' },
  unstable: { dot: 'bg-rose-400',    ring: 'ring-rose-400/30',    label: 'Ασταθές stream (server/δίκτυο)' },
};
const SPIIndicator = ({ info, stationId }) => {
  if (!info || typeof info.spi !== 'number') return null;
  const cfg = SPI_COLORS[info.label] || SPI_COLORS.stable;
  const reliability = info.reliability === 'low' ? ' · χαμηλή αξιοπιστία (CDN)' : '';
  const tip = `${cfg.label} · SPI ${info.spi.toFixed(0)}${reliability}. Μετρά σταθερότητα server/δικτύου, ΟΧΙ ακρόαση.`;
  return (
    <Tip text={tip}>
      <span
        data-testid={`spi-dot-${stationId}`}
        className={`inline-flex items-center justify-center w-2 h-2 rounded-full ${cfg.dot} ring-2 ${cfg.ring} cursor-help`}
      />
    </Tip>
  );
};

const StationRow = ({ station, isExpanded, onToggle, maxPeak, isOwned, spi }) => {
  const s = station.summary;
  const { playStation, currentStation, isPlaying } = useRadioPlayer();
  const isThisPlaying = currentStation?._id === station.station_id && isPlaying;

  const handlePlayClick = (e) => {
    e.stopPropagation();
    if (!station.stream_url) return;
    playStation({
      _id: station.station_id,
      name: station.station_name,
      stream_url: station.stream_url,
    });
  };

  return (
    <div
      className={`border rounded-lg mb-2 overflow-hidden relative transition-colors ${
        isOwned
          ? 'border-orange-500/40 bg-orange-500/[0.03]'
          : 'border-white/5'
      }`}
    >
      {isOwned && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-600" />
      )}
      <div
        data-testid={`prefecture-station-row-${station.station_id}`}
        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5 transition-colors ${isOwned ? 'pl-4' : ''}`}
        onClick={onToggle}
      >
        {/* Play button (uses station's stream_url from Stream Monitor) */}
        <button
          data-testid={`prefecture-play-${station.station_id}`}
          onClick={handlePlayClick}
          disabled={!station.stream_url}
          title={!station.stream_url ? 'Δεν υπάρχει stream URL' : (isThisPlaying ? 'Παίζει' : 'Αναπαραγωγή')}
          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
            isThisPlaying
              ? 'bg-emerald-500 hover:bg-emerald-400 text-white shadow-[0_0_0_3px_rgba(16,185,129,0.25)]'
              : station.stream_url
                ? 'bg-white/5 hover:bg-blue-500/30 text-gray-300 hover:text-blue-300'
                : 'bg-white/5 text-gray-600 cursor-not-allowed opacity-50'
          }`}
        >
          {isThisPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-[1px]" />}
        </button>

        {/* Rank + Change */}
        <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
          <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-xs font-bold text-gray-300">
            {station.rank}
          </div>
          <RankChange change={station.rank_change} stationId={station.station_id} />
        </div>

        <Radio className="w-4 h-4 text-blue-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isOwned && (
              <Tip text="Ο σταθμός σου">
                <Star className="w-3.5 h-3.5 text-orange-400 fill-orange-400/50" data-testid="own-station-star" />
              </Tip>
            )}
            <span className={`text-sm font-medium truncate ${isOwned ? 'text-orange-100' : ''}`}>{station.station_name}</span>
          </div>
          {/* Progress bar — share% for audience, activity_index for activity */}
          <div className="flex items-center gap-2 mt-0.5">
            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
              {station.display_mode === 'activity' ? (
                <div className="h-full bg-amber-500/60 rounded-full transition-all" style={{ width: `${Math.min(100, station.activity_index || 0)}%` }} />
              ) : (
                <div className="h-full bg-blue-500/60 rounded-full transition-all" style={{ width: `${Math.min(100, station.market_share || 0)}%` }} />
              )}
            </div>
            <span className="text-[10px] text-gray-500 w-12 text-right">
              {station.display_mode === 'activity'
                ? `${(station.activity_index || 0).toFixed(0)}/100`
                : `${(station.market_share || 0).toFixed(1)}%`}
            </span>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-xs text-gray-400">
          <Tip text="24ωρη τάση">
            <span className="cursor-help inline-flex items-center" data-testid={`station-sparkline-${station.station_id}`}>
              <Sparkline
                data={station.hourly}
                dataKey="avg"
                color={isOwned ? '#fb923c' : '#3b82f6'}
              />
            </span>
          </Tip>
          {station.display_mode === 'activity' ? (
            <>
              <Tip text="Δείκτης Δραστηριότητας — σύνθετο σκορ 0-100, σχετικό μεταξύ σταθμών χωρίς listener data στον ίδιο νομό. Δεν αντιστοιχεί σε ώρες ακρόασης.">
                <span className="cursor-help text-amber-400 font-bold" data-testid={`activity-index-${station.station_id}`}>
                  <BarChart3 className="w-3 h-3 inline mr-1" />{(station.activity_index || 0).toFixed(0)}
                </span>
              </Tip>
              <Tip text="Σύνολο τραγουδιών στην περίοδο">
                <span className="cursor-help">
                  <Play className="w-3 h-3 inline mr-1" />{station.activity_plays || 0}
                </span>
              </Tip>
              <Tip text={`${(station.activity_coverage || 0).toFixed(1)}% της περιόδου είχε καταγραφή — coverage`}>
                <span className="cursor-help">
                  <Clock className="w-3 h-3 inline mr-1" />{(station.activity_coverage || 0).toFixed(0)}%
                </span>
              </Tip>
              <Tip text="Ο σταθμός δεν παρέχει δεδομένα ακροατών. Υπολογισμός βάσει streaming signal.">
                <span
                  className="cursor-help inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[10px] font-semibold uppercase tracking-wide"
                  data-testid={`estimated-badge-${station.station_id}`}
                >
                  EST
                </span>
              </Tip>
            </>
          ) : (
            <>
              <Tip text={TOOLTIPS.market_share}>
                <span className={`cursor-help ${isOwned ? 'text-orange-400 font-bold text-sm' : ''}`}>
                  <PieIcon className="w-3 h-3 inline mr-1" />{station.market_share}%
                </span>
              </Tip>
              <Tip text={TOOLTIPS.tlh}>
                <span className="cursor-help"><BarChart3 className="w-3 h-3 inline mr-1" />{formatTLH(s.tlh)}</span>
              </Tip>
            </>
          )}
        </div>
        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </div>
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-white/5">
          {station.display_mode === 'activity' ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 my-2">
              <StatCard label="Plays" value={station.activity_plays || 0} icon={Play} color="amber" tooltip="Σύνολο τραγουδιών στην επιλεγμένη περίοδο" />
              <StatCard label="Plays/h" value={(station.activity_plays_per_hour || 0).toFixed(1)} icon={TrendingUp} color="purple" tooltip="Ρυθμός εκπομπής — μέσος όρος τραγουδιών ανά ώρα" />
              <StatCard label="Coverage" value={`${(station.activity_coverage || 0).toFixed(0)}%`} icon={Clock} color="green" tooltip="Ποσοστό της περιόδου με καταγεγραμμένη δραστηριότητα" />
              <Tip text="Δείκτης Δραστηριότητας 0-100. Σχετικό μέτρο μεταξύ σταθμών της ίδιας ομάδας — δεν είναι ώρες ακρόασης.">
                <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 text-amber-400 rounded-lg p-3 border border-white/5 cursor-help">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 opacity-70" />
                      <span className="text-xs opacity-70">Δραστηριότητα</span>
                    </span>
                    <span className="text-[9px] font-bold uppercase tracking-wide opacity-80">EST</span>
                  </div>
                  <div className="text-xl font-bold">{(station.activity_index || 0).toFixed(0)}<span className="text-sm opacity-60">/100</span></div>
                </div>
              </Tip>
            </div>
          ) : (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 my-2">
            <StatCard label="Peak" value={s.peak} icon={TrendingUp} color="green" tooltip={TOOLTIPS.peak} />
            <StatCard label="Avg" value={s.avg} icon={Users} color="blue" tooltip={TOOLTIPS.avg} />
            <StatCard label="Min" value={s.min} icon={TrendingDown} color="amber" tooltip={TOOLTIPS.min} />
            <StatCard label="TLH" value={formatTLH(s.tlh)} icon={BarChart3} color="purple" tooltip={TOOLTIPS.tlh} />
            <Tip text={TOOLTIPS.market_share}>
              <div className="bg-gradient-to-br from-indigo-500/20 to-indigo-600/10 text-indigo-400 rounded-lg p-3 border border-white/5 cursor-help">
                <div className="flex items-center gap-2 mb-1">
                  <PieIcon className="w-4 h-4 opacity-70" />
                  <span className="text-xs opacity-70">Μερίδιο</span>
                </div>
                <div className="text-xl font-bold">{station.market_share}%</div>
              </div>
            </Tip>
          </div>
          )}
          <MiniBarChart data={station.hourly} dataKey="avg" maxVal={s.peak} />
          <div className="flex justify-between text-[9px] text-gray-600 mt-1 px-1">
            <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
          </div>
          {/* Hourly table */}
          <div className="mt-3 max-h-48 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-900">
                <tr className="text-gray-500 border-b border-white/5">
                  <th className="text-left py-1 px-1">ΩΡΑ</th>
                  <th className="text-right py-1 px-1"><Tip text={TOOLTIPS.peak}><span className="cursor-help">PEAK</span></Tip></th>
                  <th className="text-right py-1 px-1"><Tip text={TOOLTIPS.avg}><span className="cursor-help">AVG</span></Tip></th>
                  <th className="text-right py-1 px-1"><Tip text={TOOLTIPS.min}><span className="cursor-help">MIN</span></Tip></th>
                  <th className="text-right py-1 px-1"><Tip text={TOOLTIPS.tlh}><span className="cursor-help">ACTIVITY</span></Tip></th>
                  <th className="text-right py-1 px-1"><Tip text={TOOLTIPS.samples}><span className="cursor-help">SAMPLES</span></Tip></th>
                </tr>
              </thead>
              <tbody>
                {station.hourly.filter(h => h.samples > 0).map(h => (
                  <tr key={h.hour} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-1 px-1 text-gray-400">{HOURS_LABELS[h.hour]}</td>
                    <td className="text-right py-1 px-1 text-green-400 font-medium">{h.peak}</td>
                    <td className="text-right py-1 px-1">{h.avg}</td>
                    <td className="text-right py-1 px-1 text-amber-400">{h.min}</td>
                    <td className="text-right py-1 px-1 text-purple-400">{h.tlh}</td>
                    <td className="text-right py-1 px-1 text-gray-500">{h.samples}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const daypartFullLabel = (key) => {
  const dp = DAYPARTS.find(d => d.key === key);
  if (!dp) return '';
  return dp.range ? `${dp.label} ${dp.range}` : dp.label;
};

// ─── Station Details Grid ───────────────────────────────────────────
// Shows per-station metadata (music format, group, prefecture) — distinct
// info vs the Ranking view. Deliberately excludes internal/admin fields
// (schedule, stream URLs, chart weights) from end-user visibility.

const StationDetailsCard = ({ meta, rank, isOwned }) => {
  return (
    <div
      data-testid={`station-details-card-${meta.station_id}`}
      className={`rounded-xl border p-4 bg-gradient-to-br from-white/[0.02] to-transparent transition-colors ${
        isOwned ? 'border-orange-500/40 shadow-[0_0_0_1px_rgba(251,146,60,0.15)]' : 'border-white/10'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {isOwned && <Star className="w-3.5 h-3.5 text-orange-400 fill-orange-400/60 shrink-0" />}
            <span className={`font-semibold truncate ${isOwned ? 'text-orange-100' : 'text-gray-100'}`}>
              {meta.station_name}
            </span>
            {meta.frequency && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
                {meta.frequency}
              </span>
            )}
          </div>
          {(meta.city || meta.region || meta.prefecture_label) && (
            <div className="text-[11px] text-gray-500 mt-0.5 truncate">
              {[meta.city, meta.region, meta.prefecture_label].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        {rank && (
          <div
            data-testid={`station-details-rank-${meta.station_id}`}
            className="w-7 h-7 rounded-full bg-white/5 text-gray-300 text-xs font-bold flex items-center justify-center shrink-0"
          >
            #{rank}
          </div>
        )}
      </div>

      {/* Info grid — only public-friendly fields */}
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
        {meta.music_type && (
          <div>
            <dt className="text-gray-500">Μουσική</dt>
            <dd className="text-gray-200 font-medium truncate">{meta.music_type}</dd>
          </div>
        )}
        {meta.genre && !meta.music_type && (
          <div>
            <dt className="text-gray-500">Είδος</dt>
            <dd className="text-gray-200 font-medium truncate">{meta.genre}</dd>
          </div>
        )}
        {meta.station_group && (
          <div>
            <dt className="text-gray-500">Όμιλος</dt>
            <dd className="text-gray-200 font-medium truncate">{meta.station_group}</dd>
          </div>
        )}
        <div>
          <dt className="text-gray-500">Νομός</dt>
          <dd className="text-gray-200 font-medium truncate">{meta.prefecture_label || meta.prefecture || '—'}</dd>
        </div>
      </dl>
    </div>
  );
};

const StationDetailsGrid = ({ stations, ownStationIds, ranking }) => {
  if (!stations || stations.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        Δεν υπάρχουν διαθέσιμα στοιχεία σταθμών
      </div>
    );
  }
  const rankMap = {};
  (ranking || []).forEach(r => { rankMap[r.station_id] = r.rank; });
  const sorted = [...stations].sort((a, b) => (rankMap[a.station_id] || 999) - (rankMap[b.station_id] || 999));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {sorted.map(s => (
        <StationDetailsCard
          key={s.station_id}
          meta={s}
          rank={rankMap[s.station_id]}
          isOwned={ownStationIds.has(String(s.station_id))}
        />
      ))}
    </div>
  );
};

const PrefectureReport = ({ user, dateFrom, dateTo }) => {
  const [prefectures, setPrefectures] = useState([]);
  const [selectedPrefecture, setSelectedPrefecture] = useState('');
  const [daypart, setDaypart] = useState('all');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [expandedStation, setExpandedStation] = useState(null);
  const [activeSection, setActiveSection] = useState('market_share');
  const [periodKey, setPeriodKey] = useState('current_week');
  const period = useMemo(() => {
    const preset = PERIOD_PRESETS.find(p => p.key === periodKey) || PERIOD_PRESETS[0];
    return { preset, ...computePeriodRange(preset) };
  }, [periodKey]);

  // ── Keyboard shortcuts for period selection ───────────────────────
  // W → Current week · M → Current month · ←/→ → shift weeks back/forward
  // Shortcuts ignore events fired inside inputs, selects, textareas or
  // contentEditable surfaces so typing stays unaffected.
  useEffect(() => {
    const WEEK_ORDER = ['current_week', 'previous_week', '2_weeks', '3_weeks'];
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const handler = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const key = e.key.toLowerCase();
      if (key === 'w') {
        e.preventDefault();
        setPeriodKey('current_week');
      } else if (key === 'm') {
        e.preventDefault();
        setPeriodKey('current_month');
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        // Cycle through the week-based presets only
        const currentIdx = WEEK_ORDER.indexOf(periodKey);
        if (currentIdx === -1) {
          // When on month, ← jumps to the oldest week (3_weeks), → to current_week
          setPeriodKey(e.key === 'ArrowLeft' ? '3_weeks' : 'current_week');
        } else {
          const delta = e.key === 'ArrowLeft' ? 1 : -1; // ← = further back
          const nextIdx = Math.min(WEEK_ORDER.length - 1, Math.max(0, currentIdx + delta));
          if (nextIdx !== currentIdx) setPeriodKey(WEEK_ORDER[nextIdx]);
        }
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [periodKey]);

  const userId = user?.user_id || user?.id || user?.username;
  const token = localStorage.getItem('token');

  // Fetch prefecture options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const resp = await fetch(`${backendUrl}/api/songs-analyzer/prefecture-options?user_id=${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setPrefectures(data.options || []);
          if (data.options?.length === 1) setSelectedPrefecture(data.options[0].value);
        }
      } catch (e) {
        console.error('Prefecture options error:', e);
      } finally {
        setLoadingOptions(false);
      }
    };
    if (userId) fetchOptions();
  }, [userId, token]);

  // Fetch report (uses `period` so Ranking, Market Share chart and Pie all match)
  useEffect(() => {
    if (!selectedPrefecture) { setReport(null); return; }
    const fetchReport = async () => {
      setLoading(true);
      try {
        const effectiveFrom = dateFrom || period.from;
        const effectiveTo = dateTo || period.to;
        let url = `${backendUrl}/api/songs-analyzer/prefecture-report?prefecture=${selectedPrefecture}&user_id=${userId}&daypart=${daypart}&activity_only=false`;
        url += `&date_from=${effectiveFrom}&date_to=${effectiveTo}`;
        const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (resp.ok) {
          setReport(await resp.json());
          setExpandedStation(null);
        }
      } catch (e) {
        console.error('Prefecture report error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [selectedPrefecture, dateFrom, dateTo, daypart, period.from, period.to, userId, token]);

  const maxPeak = useMemo(() => {
    if (!report?.per_station) return 0;
    return Math.max(...report.per_station.map(s => s.summary?.peak || 0), 1);
  }, [report]);

  // Own stations (based on user's assigned_stations) — highlighted in ranking
  const ownStationIds = useMemo(() => {
    const ids = user?.assigned_stations || user?.listener_stats_stations || [];
    return new Set(ids.map(String));
  }, [user]);

  // Total samples + hours coverage
  const sampleInfo = useMemo(() => {
    if (!report?.aggregate_summary) return null;
    const { samples = 0, hours_with_data = 0 } = report.aggregate_summary;
    return { samples, hours: hours_with_data, totalHours: 24 * (report?.num_days || 1) };
  }, [report]);

  // Market Share Trend data
  const [trend, setTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  // SPI map: { [station_id]: { spi, label, delta, reliability, cdn_hint } }
  const [spiMap, setSpiMap] = useState({});
  useEffect(() => {
    if (!selectedPrefecture) { setSpiMap({}); return; }
    let cancelled = false;
    const fetchSPI = async () => {
      try {
        const url = `${backendUrl}/api/stream-quality/latest?prefecture=${selectedPrefecture}`;
        const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (resp.ok) {
          const data = await resp.json();
          if (!cancelled) setSpiMap(data.items || {});
        }
      } catch (e) { /* silent — SPI is optional */ }
    };
    fetchSPI();
    const iv = setInterval(fetchSPI, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedPrefecture, token]);

  useEffect(() => {
    if (!selectedPrefecture) { setTrend(null); return; }
    const fetchTrend = async () => {
      setTrendLoading(true);
      try {
        const url = `${backendUrl}/api/songs-analyzer/prefecture-market-share-trend?prefecture=${selectedPrefecture}&user_id=${userId}&days=${period.length}`;
        const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (resp.ok) setTrend(await resp.json());
      } catch (e) {
        console.error('Trend fetch error:', e);
      } finally {
        setTrendLoading(false);
      }
    };
    fetchTrend();
  }, [selectedPrefecture, period.length, userId, token]);

  // Per-station metadata (for Στοιχεία Σταθμού cards)
  const [stationsMeta, setStationsMeta] = useState([]);
  useEffect(() => {
    if (!selectedPrefecture) { setStationsMeta([]); return; }
    const fetchMeta = async () => {
      try {
        const url = `${backendUrl}/api/songs-analyzer/prefecture-stations-meta?prefecture=${selectedPrefecture}&user_id=${userId}`;
        const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (resp.ok) {
          const data = await resp.json();
          setStationsMeta(data.stations || []);
        }
      } catch (e) { /* silent */ }
    };
    fetchMeta();
  }, [selectedPrefecture, userId, token]);

  if (loadingOptions) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-400" /></div>;
  if (prefectures.length === 0) return (
    <div className="text-center py-8 text-gray-500">
      <Radio className="w-8 h-8 mx-auto mb-2 opacity-50" />
      <p>Δεν υπάρχουν νομοί με σταθμούς. Αναθέστε νομό στο Edit Panel.</p>
    </div>
  );

  return (
    <div data-testid="prefecture-report" className="space-y-4">
      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          data-testid="prefecture-selector"
          value={selectedPrefecture}
          onChange={(e) => setSelectedPrefecture(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-white/10 bg-gray-800 text-gray-100 min-w-[200px]"
        >
          <option value="">-- Επιλέξτε Νομό --</option>
          {prefectures.map(p => (
            <option key={p.value} value={p.value}>
              {p.label} {p.stations_count ? `(${p.stations_count})` : ''}
            </option>
          ))}
        </select>

        {/* Daypart buttons — distinct pill buttons with icon + range */}
        <div
          data-testid="daypart-group"
          className="flex flex-wrap gap-1.5"
        >
          {DAYPARTS.map(dp => {
            const Icon = dp.icon;
            const isActive = daypart === dp.key;
            return (
              <button
                key={dp.key}
                data-testid={`daypart-btn-${dp.key}`}
                onClick={() => setDaypart(dp.key)}
                aria-pressed={isActive}
                className={[
                  'group inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap',
                  'border transition-all duration-150 select-none',
                  isActive
                    ? 'bg-indigo-500/20 text-indigo-200 border-indigo-400/60 shadow-[0_0_0_1px_rgba(99,102,241,0.35)] ring-1 ring-indigo-400/30'
                    : 'bg-white/[0.03] text-gray-300 border-white/10 hover:bg-white/[0.07] hover:text-white hover:border-white/25 active:scale-[0.97]',
                ].join(' ')}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-indigo-300' : 'text-gray-400 group-hover:text-gray-200'}`} />
                <span className="font-medium">{dp.short}</span>
                {dp.range && (
                  <span className={`text-[10px] tabular-nums ${isActive ? 'text-indigo-300/80' : 'text-gray-500'}`}>
                    {dp.range}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {report && <span className="text-xs text-gray-500">{report.stations_count} σταθμοί</span>}
      </div>

      {/* Period presets row */}
      <div data-testid="period-group" className="flex flex-wrap items-center gap-1.5">
        {PERIOD_PRESETS.map(preset => {
          const isActive = periodKey === preset.key;
          // Map preset → shortcut key hint
          const shortcut = preset.key === 'current_week' ? 'W'
                         : preset.key === 'current_month' ? 'M'
                         : null;
          const title = shortcut
            ? `Πλήκτρο: ${shortcut}`
            : (preset.key === 'previous_week' || preset.key === '2_weeks' || preset.key === '3_weeks')
              ? 'Πλήκτρα: ← / → για εναλλαγή εβδομάδων'
              : undefined;
          return (
            <button
              key={preset.key}
              data-testid={`period-btn-${preset.key}`}
              onClick={() => setPeriodKey(preset.key)}
              aria-pressed={isActive}
              title={title}
              className={`group px-2.5 py-1 text-[11px] rounded-full border whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
                isActive
                  ? 'bg-blue-500/20 text-blue-200 border-blue-500/50'
                  : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-gray-200'
              }`}
            >
              {preset.label}
              {shortcut && (
                <kbd
                  className={`ml-0.5 text-[9px] font-mono leading-none px-1 py-[2px] rounded border ${
                    isActive
                      ? 'border-blue-400/60 bg-blue-500/20 text-blue-100'
                      : 'border-white/15 bg-black/20 text-gray-400 group-hover:border-white/30 group-hover:text-gray-200'
                  }`}
                  aria-label={`Συντόμευση ${shortcut}`}
                >
                  {shortcut}
                </kbd>
              )}
            </button>
          );
        })}
        <span className="text-[10px] text-gray-500 ml-2">{period.from} → {period.to}</span>
        <span
          className="hidden sm:inline-flex items-center gap-1 text-[10px] text-gray-500 ml-auto"
          title="W = Τρέχουσα εβδομάδα · M = Τρέχων μήνας · ←/→ = Εναλλαγή εβδομάδων"
        >
          <kbd className="font-mono px-1 py-[1px] rounded border border-white/10 bg-black/20">←</kbd>
          <kbd className="font-mono px-1 py-[1px] rounded border border-white/10 bg-black/20">→</kbd>
          <span>εβδομάδες</span>
        </span>
      </div>

      {loading && <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-400" /></div>}

      {report && !loading && (
        <div className="flex gap-3 sm:gap-4">
          {/* Vertical Sidebar (sm+) / Horizontal scroll (mobile) */}
          <div
            data-testid="prefecture-sidebar"
            className="flex sm:flex-col sm:w-32 md:w-40 shrink-0 gap-1 overflow-x-auto sm:overflow-visible scrollbar-thin pb-1 sm:pb-0"
          >
            {SECTIONS.map(sec => {
              const Icon = sec.icon;
              const isActive = activeSection === sec.key;
              return (
                <button
                  key={sec.key}
                  data-testid={`section-btn-${sec.key}`}
                  onClick={() => setActiveSection(sec.key)}
                  className={`flex sm:flex-col items-center gap-2 sm:gap-1.5 px-3 sm:px-2 py-2.5 rounded-lg text-xs transition-all whitespace-nowrap border ${
                    isActive
                      ? 'bg-blue-500/15 text-blue-300 border-blue-500/40 shadow-[inset_3px_0_0_#3b82f6] sm:shadow-[inset_3px_0_0_#3b82f6]'
                      : 'bg-white/[0.02] text-gray-400 border-transparent hover:bg-white/5 hover:text-gray-200'
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-blue-400' : ''}`} />
                  <span className="text-[11px] sm:text-center leading-tight">{sec.label}</span>
                </button>
              );
            })}
          </div>

          {/* Main content area */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* ── Market Share section ── */}
            {activeSection === 'market_share' && (
              <>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs text-gray-400 flex items-center gap-2">
                    <span className="text-gray-500">{report.prefecture_label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30">
                      {period.preset.label}
                    </span>
                    {daypart !== 'all' && <span className="text-indigo-400">· {daypartFullLabel(daypart)}</span>}
                  </div>
                  {sampleInfo && (
                    <Tip text={`${sampleInfo.samples.toLocaleString('el-GR')} συνολικές μετρήσεις · κάλυψη ${sampleInfo.hours} ωρών`}>
                      <div data-testid="sample-size-indicator" className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[11px] text-gray-400 cursor-help">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span>Δείγμα: <span className="text-gray-200 font-semibold">{sampleInfo.samples.toLocaleString('el-GR')}</span></span>
                        <span className="text-gray-600">·</span>
                        <span>Κάλυψη: <span className="text-gray-200 font-semibold">{sampleInfo.hours}ώ</span></span>
                      </div>
                    </Tip>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard label="Peak" value={report.aggregate_summary.peak} icon={TrendingUp} color="green" tooltip={TOOLTIPS.peak} />
                  <StatCard label="Avg" value={report.aggregate_summary.avg} icon={Users} color="blue" tooltip={TOOLTIPS.avg} />
                  <StatCard label="Min" value={report.aggregate_summary.min} icon={TrendingDown} color="amber" tooltip={TOOLTIPS.min} />
                  <StatCard label="Activity Index" value={formatTLH(report.aggregate_summary.tlh)} icon={BarChart3} color="purple" tooltip={TOOLTIPS.tlh} />
                </div>

                {/* Market Share Trend (day-by-day) + Aggregate Pie */}
                <div className="bg-gray-800/50 rounded-lg border border-white/5">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/5">
                    <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                      <BarChart3 className="w-4 h-4" />
                      Μερίδιο Αγοράς — Ημέρα προς Ημέρα
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3 p-2">
                    <div>
                      {trendLoading ? (
                        <div className="flex items-center justify-center py-8"><Loader2 className="w-4 h-4 animate-spin text-blue-400" /></div>
                      ) : trend?.days?.length > 0 ? (
                        <MarketShareTrend data={trend.days} stations={trend.stations || []} ownIds={ownStationIds} />
                      ) : (
                        <div className="text-center py-6 text-xs text-gray-500">Δεν υπάρχουν δεδομένα trend</div>
                      )}
                    </div>
                    <MarketShareAveragePie stations={report.per_station} />
                  </div>
                </div>
              </>
            )}

            {/* ── Ranking section ── */}
            {activeSection === 'ranking' && (() => {
              const audienceStations = report.per_station.filter(s => s.display_mode === 'audience');
              const activityStations = report.per_station.filter(s => s.display_mode === 'activity');
              const isMixed = audienceStations.length > 0 && activityStations.length > 0;
              return (
                <>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                      <Award className="w-4 h-4" />
                      Κατάταξη Σταθμών ({report.per_station.length})
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30 font-normal">
                        {period.preset.label}
                      </span>
                    </div>
                    {daypart !== 'all' && <span className="text-[11px] text-indigo-400">{daypartFullLabel(daypart)}</span>}
                  </div>

                  {/* When mixed, render two clearly separated honest sections.
                      When pure (single group), keep the original single list. */}
                  {isMixed ? (
                    <>
                      {/* 🔴 Live Audience — real listener data, TLH/share visible */}
                      <div className="mt-3" data-testid="audience-section">
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-red-400">
                            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                            Live Audience
                          </span>
                          <span className="text-[10px] text-gray-500">
                            ({audienceStations.length} σταθμοί με πραγματικά δεδομένα ακροατών)
                          </span>
                        </div>
                        {audienceStations.map(station => (
                          <StationRow
                            key={station.station_id}
                            station={station}
                            isExpanded={expandedStation === station.station_id}
                            onToggle={() => setExpandedStation(expandedStation === station.station_id ? null : station.station_id)}
                            maxPeak={maxPeak}
                            isOwned={ownStationIds.has(String(station.station_id))}
                          />
                        ))}
                      </div>

                      {/* 📻 Broadcast Activity — no listener data, honest activity metrics */}
                      <div className="mt-6 pt-4 border-t border-white/5" data-testid="activity-section">
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-400">
                            <Radio className="w-3 h-3" />
                            Broadcast Activity
                          </span>
                          <span className="text-[10px] text-gray-500">
                            ({activityStations.length} σταθμοί χωρίς listener data — μετρικές δραστηριότητας)
                          </span>
                        </div>
                        {activityStations.map(station => (
                          <StationRow
                            key={station.station_id}
                            station={station}
                            isExpanded={expandedStation === station.station_id}
                            onToggle={() => setExpandedStation(expandedStation === station.station_id ? null : station.station_id)}
                            maxPeak={maxPeak}
                            isOwned={ownStationIds.has(String(station.station_id))}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div>
                      {report.per_station.map(station => (
                        <StationRow
                          key={station.station_id}
                          station={station}
                          isExpanded={expandedStation === station.station_id}
                          onToggle={() => setExpandedStation(expandedStation === station.station_id ? null : station.station_id)}
                          maxPeak={maxPeak}
                          isOwned={ownStationIds.has(String(station.station_id))}
                        />
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {/* ── Daypart Activity section ── */}
            {activeSection === 'daypart' && (
              <>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs text-gray-400 flex items-center gap-2">
                    <span className="text-gray-500">Ωριαία δραστηριότητα · {report.prefecture_label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30">
                      {period.preset.label}
                    </span>
                    {daypart !== 'all' && <span className="text-indigo-400">· {daypartFullLabel(daypart)}</span>}
                  </div>
                </div>

                {/* Aggregate Chart */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-white/5">
                  <div className="text-xs text-gray-400 mb-2">
                    Συνολικά {report.prefecture_label} — Ωριαίο AVG
                  </div>
                  <MiniBarChart data={report.aggregate_hourly} dataKey="avg" height={110} />
                  <div className="flex justify-between text-[9px] text-gray-600 mt-1 px-1">
                    <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
                  </div>
                </div>

                {/* Aggregate Hourly Table */}
                <div className="bg-gray-800/50 rounded-lg border border-white/5 overflow-hidden">
                  <div className="px-3 py-2 border-b border-white/5 text-xs text-gray-400 font-medium">
                    Ωριαία Στατιστικά — {report.prefecture_label}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-800">
                        <tr className="text-gray-500 border-b border-white/5">
                          <th className="text-left py-1.5 px-2">ΩΡΑ</th>
                          <th className="text-right py-1.5 px-2"><Tip text={TOOLTIPS.peak}><span className="cursor-help">PEAK</span></Tip></th>
                          <th className="text-right py-1.5 px-2"><Tip text={TOOLTIPS.avg}><span className="cursor-help">AVG</span></Tip></th>
                          <th className="text-right py-1.5 px-2"><Tip text={TOOLTIPS.min}><span className="cursor-help">MIN</span></Tip></th>
                          <th className="text-right py-1.5 px-2"><Tip text={TOOLTIPS.tlh}><span className="cursor-help">TLH</span></Tip></th>
                          <th className="text-right py-1.5 px-2"><Tip text={TOOLTIPS.samples}><span className="cursor-help">SAMPLES</span></Tip></th>
                          <th className="text-right py-1.5 px-2"><Tip text={TOOLTIPS.stations}><span className="cursor-help">ΣΤΑΘΜΟΙ</span></Tip></th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.aggregate_hourly.filter(h => h.samples > 0).map(h => (
                          <tr key={h.hour} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-1 px-2 text-gray-400 font-medium">{HOURS_LABELS[h.hour]}</td>
                            <td className="text-right py-1 px-2 text-green-400 font-medium">{h.peak}</td>
                            <td className="text-right py-1 px-2">{h.avg}</td>
                            <td className="text-right py-1 px-2 text-amber-400">{h.min}</td>
                            <td className="text-right py-1 px-2 text-purple-400">{h.tlh}</td>
                            <td className="text-right py-1 px-2 text-gray-500">{h.samples}</td>
                            <td className="text-right py-1 px-2 text-blue-400">{h.stations}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* ── Station Details section ── */}
            {activeSection === 'station_details' && (
              <StationDetailsGrid
                stations={stationsMeta}
                ownStationIds={ownStationIds}
                ranking={report.per_station}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrefectureReport;
